import { useEffect, useState } from 'react';
import {
  DefaultGenerics,
  ExtendableGenerics,
  OwnUserResponse,
  StreamChat,
  TokenOrProvider,
  UserResponse,
} from 'stream-chat';

/**
 * A hook which handles the process of connecting/disconnecting a user
 * to the Stream Chat backend.
 *
 * @param apiKey the Stream app API key to use.
 * @param userToConnect the user information.
 * @param userTokenOrProvider the user's token.
 */
export const useConnectUser = <SCG extends ExtendableGenerics = DefaultGenerics>(
  apiKey: string,
  userToConnect: OwnUserResponse<SCG> | UserResponse<SCG>,
  userTokenOrProvider: TokenOrProvider,
) => {
  const [chatClient, setChatClient] = useState<StreamChat<SCG> | null>(null);
  
  useEffect(() => {
    if (!apiKey || !userTokenOrProvider) {
      return undefined;
    }

    const client = new StreamChat<SCG>(apiKey, {
      enableInsights: true,
      enableWSFallback: true,
    });

    let cancelled = false;

    const connectPromise = client
      .connectUser(userToConnect, userTokenOrProvider)
      .then(() => {
        if (!cancelled) {
          setChatClient(client);
        }
      })
      .catch((e) => {
        console.error(`Failed to connect user`, e);
      });

    return () => {
      cancelled = true;
      setChatClient((current) => (current === client ? null : current));
      connectPromise.finally(() => {
        client.disconnectUser().catch((e) => {
          console.error(`Failed to disconnect user`, e);
        });
      });
    };
  }, [apiKey, userTokenOrProvider, userToConnect]);

  return chatClient;
};
